import { print, type DocumentNode } from 'graphql'
import {
  MissingGraphQLUrlError,
  GraphQLHttpError,
  GraphQLServerError,
  GraphQLResponseParseError,
  NetworkError,
  UninitializedSDKError,
} from './errors.js'

/**
 * Minimal interface for GraphQL execution.
 *
 * Any GraphQL client can implement this interface:
 * - Apollo Client
 * - urql
 * - graphql-request
 * - Graffle
 * - etc.
 */
export interface GqlExecutor {
  query: <TData = unknown, TVariables = Record<string, unknown>>(
    document: DocumentNode,
    variables?: TVariables
  ) => Promise<TData>

  mutate: <TData = unknown, TVariables = Record<string, unknown>>(
    document: DocumentNode,
    variables?: TVariables
  ) => Promise<TData>
}

/**
 * Configuration for creating a fetch-based executor.
 *
 * Auth can be provided via:
 * - `adminSecret`: Convenience option that adds the `x-hasura-admin-secret` header
 * - `headers`: Custom headers for JWT tokens, API keys, or other auth mechanisms
 *
 * Both can be used together - adminSecret will be merged into headers.
 */
export interface ExecutorConfig {
  /** GraphQL endpoint URL */
  url: string
  /** Hasura admin secret - automatically adds x-hasura-admin-secret header */
  adminSecret?: string
  /** Additional headers for authentication and other purposes */
  headers?: Record<string, string>
}

// Application-scoped default executor override (set via init)
let defaultExecutorOverride: GqlExecutor | null = null

/**
 * GraphQL error from the server.
 */
export interface GraphQLError {
  message: string
  locations?: Array<{ line: number; column: number }>
  path?: Array<string | number>
  extensions?: Record<string, unknown>
}

/**
 * GraphQL response shape.
 */
interface GraphQLResponse<TData> {
  data?: TData
  errors?: GraphQLError[]
}

/**
 * Execute a GraphQL operation using fetch.
 */
async function executeGraphQL<TData, TVariables>(
  url: string,
  headers: Record<string, string>,
  document: DocumentNode,
  variables?: TVariables
): Promise<TData> {
  const query = print(document)

  let response: Response
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...headers,
      },
      body: JSON.stringify({
        query,
        variables,
      }),
    })
  } catch (error) {
    throw new NetworkError(error, url)
  }

  if (!response.ok) {
    throw new GraphQLHttpError(response.status, response.statusText, url)
  }

  let result: GraphQLResponse<TData>
  try {
    result = (await response.json()) as GraphQLResponse<TData>
  } catch (error) {
    throw new GraphQLResponseParseError(error, url)
  }

  if (result.errors && result.errors.length > 0) {
    throw new GraphQLServerError(result.errors)
  }

  return result.data as TData
}

/**
 * Create a new fetch-based executor.
 *
 * @example With Hasura admin secret (recommended)
 * ```typescript
 * createFetchExecutor({
 *   url: 'https://gql.hopdrive.io/v1/graphql',
 *   adminSecret: adminSecret // From your app's configuration
 * })
 * ```
 *
 * @example With JWT token
 * ```typescript
 * createFetchExecutor({
 *   url: 'https://gql.hopdrive.io/v1/graphql',
 *   headers: { 'Authorization': `Bearer ${jwtToken}` }
 * })
 * ```
 *
 * @example With admin secret and additional headers
 * ```typescript
 * createFetchExecutor({
 *   url: 'https://gql.hopdrive.io/v1/graphql',
 *   adminSecret: adminSecret, // From your app's configuration
 *   headers: { 'X-Custom-Header': 'value' }
 * })
 * ```
 */
export function createFetchExecutor(config: ExecutorConfig): GqlExecutor {
  if (!config.url) {
    throw new MissingGraphQLUrlError()
  }

  // Build headers: start with provided headers, then add admin secret if provided
  const headers: Record<string, string> = {
    ...config.headers,
  }

  // Add admin secret header if provided (after spreading headers so it takes precedence)
  if (config.adminSecret) {
    headers['x-hasura-admin-secret'] = config.adminSecret
  }

  return {
    query: async (document, variables) => {
      return executeGraphQL(config.url, headers, document, variables)
    },
    mutate: async (document, variables) => {
      return executeGraphQL(config.url, headers, document, variables)
    },
  }
}

/**
 * Initialize the SDK with a default executor.
 * This executor will be used for all subsequent calls when no executor is explicitly provided.
 * Should be called once at application startup.
 *
 * @param options - Initialization options
 * @param options.executor - The executor to use as the default for all SDK operations
 *
 * @example
 * ```typescript
 * init({
 *   executor: createFetchExecutor({
 *     url: graphqlUrl,      // From your app's configuration
 *     adminSecret: adminSecret
 *   })
 * })
 * ```
 */
export function init(options: { executor: GqlExecutor }): void {
  defaultExecutorOverride = options.executor
}

/**
 * Resolve the executor to use.
 * Priority:
 * 1. Explicitly provided executor
 * 2. Default executor override (set via init)
 * 3. Throws UninitializedSDKError if neither is available
 */
export function resolveExecutor(executor?: GqlExecutor): GqlExecutor {
  if (executor) return executor
  if (defaultExecutorOverride) return defaultExecutorOverride
  throw new UninitializedSDKError()
}

/**
 * Reset the default executor override (useful for testing).
 */
export function resetDefaultExecutor(): void {
  defaultExecutorOverride = null
}
